// Severity story (lyse 8.0.0 / UX Q6) — the canonical defect grade chip.
//
// UX Q6 (decided 2026-06-11): the class-model's own grade labels + colours
// are canonical in ALL inspector UI — what the DefectDrawer's Severity
// radios already show (the grade NUMBER, `classCategory`). The wire carries
// everything needed per row (`classCategory` + `classColor`), so no extra
// class-model round-trip is required to render:
//
//   * graded   → chip labelled with the grade number, filled with the
//                class colour (falling back to the derived-severity palette
//                when the row carries no colour),
//   * legacy   → rows with a derived `severity` but no `classCategory`
//                (pre-8.0.0 fixtures / degraded projections) keep the enum
//                label so old harnesses stay meaningful,
//   * ungraded → `severity === null` renders the neutral "Not graded" chip —
//                NEVER a fabricated grade.
import { SeverityChip } from "@aeriosiq/ui";
import type { Defect } from "@aeriosiq/types";

export interface DefectGradeChipProps {
  defect: Pick<Defect, "severity"> & {
    classCategory?: number | null;
    classColor?: string | null;
  };
  dense?: boolean;
}

/**
 * The grade label shown for a defect (also used for accessible names):
 * `"grade N"` when the canonical grade is on the row, the derived enum for
 * legacy rows, `"not graded"` for ungraded rows.
 */
export function defectGradeText(
  defect: DefectGradeChipProps["defect"],
): string {
  if (defect.classCategory != null)
    return `grade ${String(defect.classCategory)}`;
  return defect.severity ?? "not graded";
}

export function DefectGradeChip({
  defect,
  dense = false,
}: DefectGradeChipProps): React.ReactElement {
  if (defect.classCategory != null) {
    return (
      <SeverityChip
        severity={defect.severity ?? null}
        label={String(defect.classCategory)}
        {...(defect.classColor ? { colorOverride: defect.classColor } : {})}
        ariaLabel={`Severity grade ${String(defect.classCategory)}`}
        dense={dense}
      />
    );
  }
  return <SeverityChip severity={defect.severity ?? null} dense={dense} />;
}
